/*
 * IOSDiagnostic — temporary on-screen debug panel for TestFlight troubleshooting.
 *
 * Purpose
 * -------
 * When a native IAP flow fails silently inside the WKWebView (bridge not
 * registered, product not approved, wrong sandbox account, etc.) the React
 * app has no way to surface the underlying reason — a toast may be missed,
 * console logs are invisible to a physical device tester, and StoreKit
 * errors get swallowed by the Swift → JS bridge. This panel fixes that by:
 *
 *   1. Detecting the runtime platform (must be "ios" for the compliant
 *      build to work correctly).
 *   2. Checking whether the SelahIAPBridge Swift handler is registered on
 *      the WKWebView (i.e. window.webkit.messageHandlers.selahIAP exists).
 *   3. Running a preflight `getProducts` call against StoreKit so we know
 *      before the user taps Subscribe whether Apple can even resolve our
 *      product ID for this Sandbox account.
 *   4. Exposing a global `window.__iapDebug(msg)` function so the purchase
 *      code paths (Paywall.jsx, Settings.jsx, TrialBanner.jsx, iapBridge.js)
 *      can push a live event log the tester can screenshot.
 *
 * Activation
 * ----------
 *   - Automatically visible on iOS builds (isIOS() === true).
 *   - Force-visible on ANY platform via `?_debug=1` in the URL (useful for
 *     verifying the panel itself works in the preview environment before
 *     redeploying to production).
 *   - User-dismissible with the small "×" — the diagnostic stays hidden
 *     until the app is reopened.
 *
 * Removal
 * -------
 * This file is deliberately isolated so the entire diagnostic can be
 * removed by:
 *   1. Deleting this file.
 *   2. Removing `<IOSDiagnostic />` and its import from App.js.
 *   3. Removing the `if (window.__iapDebug) window.__iapDebug(...)` calls
 *      from iapBridge.js / Paywall.jsx / Settings.jsx / TrialBanner.jsx.
 *
 * DO NOT ship this to production long-term — it exposes internal state.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getPlatformTarget, isIOS } from '../utils/platform';
import * as iap from '../utils/iapBridge';

const APPLE_PRODUCT_ID =
  process.env.REACT_APP_APPLE_PRODUCT_ID || 'com.selahdownloads.subscription.monthly';

/**
 * Renders the floating diagnostic panel. Should be mounted once, at the app
 * root, so its global `window.__iapDebug` hook is available before any
 * paywall / subscribe flow runs.
 */
export default function IOSDiagnostic() {
  const [visible, setVisible] = useState(true);
  // Start collapsed by default so the panel is a slim, non-blocking chip
  // that cannot cover the Subscribe / Restore buttons on the paywall or
  // trial banner. The tester expands it after tapping the CTA to inspect
  // the resulting event log.
  const [expanded, setExpanded] = useState(false);
  const [log, setLog] = useState(() => {
    // Restore any log entries that were persisted before the WebView
    // last reloaded. This is critical for debugging the "pink launch
    // screen" bug where a bad navigation reloads the app and destroys
    // in-memory React state before we can screenshot the log.
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem('__iap_diagnostic_log');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Mark restored entries with a prefix so a tester can tell
          // them apart from the current session's fresh events.
          return parsed.map((line) => `♻ ${line}`);
        }
      }
    } catch (_e) {
      /* corrupted storage — ignore and start fresh */
    }
    return [];
  });
  const [platform, setPlatform] = useState('');
  const [bridgeAvailable, setBridgeAvailable] = useState(null);
  const [productStatus, setProductStatus] = useState('checking…');
  const [ua, setUa] = useState('');

  const pushLog = useCallback((msg) => {
    setLog((prev) => {
      const line = `${new Date().toLocaleTimeString()}  ${msg}`;
      // Keep the last 20 entries so the panel doesn't grow unbounded
      // (was 12 — bumped so we don't drop persisted history after
      // several sessions of debugging).
      const next = [...prev.slice(-19), line];
      // Persist current session events (drop the "♻" restored prefix so
      // the next reload doesn't accumulate arrows).
      try {
        if (typeof window !== 'undefined') {
          const strippable = next.map((l) => l.replace(/^♻ /, ''));
          window.localStorage.setItem(
            '__iap_diagnostic_log',
            JSON.stringify(strippable.slice(-20))
          );
        }
      } catch (_e) {
        /* localStorage full or blocked — safe to ignore */
      }
      return next;
    });
  }, []);

  // Decide whether to render. We compute this INSIDE useEffect so SSR safe.
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let debugForced = false;
    try {
      debugForced =
        new URLSearchParams(window.location.search).get('_debug') === '1';
    } catch (_e) {
      /* URL parsing can throw in weird sandboxes — treat as no override. */
    }
    // Only render when the debug flag is explicitly opted in via the URL.
    // We intentionally do NOT auto-render on iOS anymore — Apple's App
    // Review reviewer would otherwise see the internal diagnostic HUD,
    // which is not appropriate for a shipping build. To re-enable during
    // TestFlight debugging: navigate to https://selahdownloads.com/?_debug=1
    // inside the app (or set the URL in the wrapper's loadRootUrl).
    const enable = debugForced;
    setShouldRender(enable);
    if (!enable) return;

    // Snapshot environment.
    setPlatform(getPlatformTarget());
    setUa((navigator?.userAgent || '').slice(0, 140));
    const bridge = iap.isNativeIAPAvailable();
    setBridgeAvailable(bridge);
    pushLog(
      bridge
        ? 'Native IAP bridge FOUND (window.webkit.messageHandlers.selahIAP)'
        : 'Native IAP bridge MISSING'
    );

    // Wire the global logger for other modules.
    window.__iapDebug = (msg) => {
      try {
        pushLog(String(msg).slice(0, 240));
      } catch (_e) {
        /* Swallow — never let a diagnostic call break production paths. */
      }
    };

    // Preflight: ask Apple whether it can even see our product.
    if (bridge) {
      pushLog(`Preflight → getProducts(['${APPLE_PRODUCT_ID}'])`);
      iap
        .getProducts([APPLE_PRODUCT_ID])
        .then((r) => {
          if (!r?.ok) {
            setProductStatus(
              `getProducts failed: ${r?.error || 'no response'}`
            );
            pushLog(`Preflight ERROR: ${r?.error || 'no response'}`);
            return;
          }
          const products = Array.isArray(r.products) ? r.products : [];
          if (products.length === 0) {
            setProductStatus(
              `No products returned for id "${APPLE_PRODUCT_ID}" — check App Store Connect approval / bundle-id mapping.`
            );
            pushLog('Preflight OK but products=[] (Apple has no matching SKU)');
            return;
          }
          const first = products[0];
          setProductStatus(
            `OK — ${first.priceLocalized} · ${first.title}`
          );
          pushLog(
            `Preflight OK: ${first.id} ${first.priceLocalized}`
          );
        })
        .catch((err) => {
          setProductStatus(`getProducts threw: ${String(err).slice(0, 160)}`);
          pushLog(`Preflight EXCEPTION: ${String(err).slice(0, 160)}`);
        });
    } else {
      setProductStatus('cannot check — bridge missing');
    }

    return () => {
      // Leave the logger in place across route changes so late events (e.g.
      // Apple purchase result arriving after navigation) still get captured.
    };
  }, [pushLog]);

  if (!shouldRender || !visible) return null;

  return (
    <div
      data-testid="ios-diagnostic-panel"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        // Anchor to the top-left corner so it never covers the Subscribe
        // CTA on the trial banner or paywall (both are anchored to the
        // right side or the vertical centre of the viewport).
        top: 8,
        left: 8,
        // When collapsed we don't stretch full-width — keep the chip
        // compact so more of the underlying UI is tappable.
        right: expanded ? 8 : 'auto',
        maxWidth: expanded ? 420 : 220,
        zIndex: 9999,
        background: 'rgba(18, 18, 18, 0.94)',
        color: '#FDFBF7',
        border: '1px solid rgba(212,175,55,0.35)',
        borderRadius: 10,
        padding: expanded ? '10px 12px 12px' : '5px 10px',
        fontFamily:
          '"Source Code Pro", ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 11,
        lineHeight: 1.35,
        boxShadow: '0 10px 30px -12px rgba(0,0,0,0.6)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: expanded ? 6 : 0,
        }}
      >
        <span
          style={{
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#D4AF37',
            fontSize: 10.5,
            textTransform: 'uppercase',
          }}
        >
          {expanded ? 'Selah IAP Diagnostic' : `IAP · ${log.length} evt`}
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={() => {
              setLog([]);
              try {
                window.localStorage.removeItem('__iap_diagnostic_log');
              } catch (_e) { /* ignore */ }
            }}
            data-testid="ios-diagnostic-clear"
            style={{
              background: 'transparent',
              color: 'rgba(253,251,247,0.6)',
              border: '1px solid rgba(253,251,247,0.2)',
              borderRadius: 6,
              padding: '2px 8px',
              fontSize: 10,
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="ios-diagnostic-toggle"
            style={{
              background: 'transparent',
              color: '#D2B48C',
              border: '1px solid rgba(210,180,140,0.35)',
              borderRadius: 6,
              padding: '2px 8px',
              fontSize: 10,
              cursor: 'pointer',
            }}
          >
            {expanded ? 'Hide' : 'Show'}
          </button>
          <button
            type="button"
            onClick={() => setVisible(false)}
            data-testid="ios-diagnostic-dismiss"
            aria-label="Dismiss diagnostic"
            style={{
              background: 'transparent',
              color: 'rgba(253,251,247,0.6)',
              border: '1px solid rgba(253,251,247,0.2)',
              borderRadius: 6,
              padding: '2px 8px',
              fontSize: 10,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
      </div>

      {expanded && (
        <>
          <Row label="Platform">
            <span
              style={{
                color: platform === 'ios' ? '#7FD3A2' : '#F0A5A5',
                fontWeight: 700,
              }}
            >
              {platform || '(unknown)'}
            </span>
          </Row>
          <Row label="Bridge">
            <span
              style={{
                color: bridgeAvailable ? '#7FD3A2' : '#F0A5A5',
                fontWeight: 700,
              }}
            >
              {bridgeAvailable ? 'available' : 'missing'}
            </span>
          </Row>
          <Row label="Product">
            <span
              style={{
                color: productStatus.startsWith('OK') ? '#7FD3A2' : '#F5C57A',
              }}
            >
              {productStatus}
            </span>
          </Row>
          <Row label="UA">
            <span style={{ color: 'rgba(253,251,247,0.65)' }}>
              {ua || '(none)'}
            </span>
          </Row>

          <div
            style={{
              marginTop: 8,
              paddingTop: 6,
              borderTop: '1px solid rgba(253,251,247,0.08)',
              color: 'rgba(253,251,247,0.55)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Event log
          </div>
          <div
            data-testid="ios-diagnostic-log"
            style={{
              marginTop: 4,
              maxHeight: 130,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: 'rgba(253,251,247,0.85)',
            }}
          >
            {log.length === 0 ? (
              <span style={{ color: 'rgba(253,251,247,0.4)' }}>
                (no events yet — tap Subscribe to trigger)
              </span>
            ) : (
              log.map((line, idx) => (
                <div key={idx} style={{ padding: '1px 0' }}>
                  {line}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
      <span
        style={{
          minWidth: 60,
          color: 'rgba(253,251,247,0.55)',
        }}
      >
        {label}:
      </span>
      <span style={{ flex: 1, wordBreak: 'break-word' }}>{children}</span>
    </div>
  );
}
